<template>
    <div class="sticky top-0 flex flex-col lg:flex-row justify-between items-start">
        <h2 class="text-xl leading-9 bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text font-medium font-headline antialiased ">Info</h2>
        <nav v-if="GlobalPolicy.edit()" class="flex items-end gap-2">
            <FormButton @click="edit()" v-if="!editing" icon="fa-pen" icon-size="text-lg" prefix-class="fak" color="blue">Edit</FormButton>
            <template v-else>
                <FormButton @click="cancel()" color="gray">Cancel</FormButton>
                <FormButton :disabled="!valid" @click="save" color="green">Save Changes</FormButton>
            </template>
        </nav>

    </div>
    <p class="text-sm text-bhav-bright-blue-500 bg-bhav-bright-blue-100 p-2 mt-4 rounded">
        <span class="fak fa-alert mr-2"></span>
        <strong>NEW FEATURE!</strong> When edited and saved, data here gets pushed to the Kova Web Tab.
    </p>
    <dl class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-if="!editing">
        <dt class="font-normal text-bhav-blue-200 py-4 pr-4">Marketing Headline</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal">{{ salesOrder.meta?.info?.headline }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Slug</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ salesOrder.slug }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Property Description</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ salesOrder.meta?.info?.description }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Price</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ formatToCurrency(salesOrder.meta?.info?.price) }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Advertised Rate</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ salesOrder.meta?.info?.rate }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Square Feet</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ new Intl.NumberFormat('en-US', {}).format(salesOrder.meta?.info?.sqft) }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Bedrooms</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ salesOrder.meta?.info?.bedrooms }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Full Bathrooms</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ salesOrder.meta?.info?.bathrooms?.full }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Half Bathrooms</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ salesOrder.meta?.info?.bathrooms?.half }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Garages</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ salesOrder.meta?.info?.garages }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Geolocation</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">
            <p>Latitude: {{ salesOrder.meta.info?.geo?.lat }}º</p>
            <p>Longitude: {{ salesOrder.meta.info?.geo?.lng }}º</p>
        </dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Callout</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ salesOrder.meta.info.callout !== 'N/S' ? salesOrder.meta?.info?.callout : '' }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Landing Page URL</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50"><a class="text-bhav-blue-500 hover:underline" :href="salesOrder.meta.info.website" target="_blank" title="Visit this home's website">{{ salesOrder.meta.info.website }}</a></dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Internal Notes</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ salesOrder.meta.info?.notes }}</dd>
    </dl>

    <fieldset class="grid grid-cols-1 lg:grid-cols-4 mt-8" v-else>
        <legend class="sr-only">Custom Properties</legend>

        <FormRow for="info.headline" label="Marketing Headline" :valid="valid('meta.info.headline')">
            <TextInput id="info.headline" v-model="item.meta.info.headline" maxlength="100" size="100" />
        </FormRow>

        <FormRow for="slug" label="Slug" :valid="valid('slug')">
            <TextInput id="slug" v-model="item.slug" maxlength="100" size="100" />
        </FormRow>

        <FormRow for="info.description" label="Property Description" :valid="valid('meta.info.description')">
            <textarea :maxlength="1500" class="bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-48" id="info.description" v-model="item.meta.info.description" maxlength="2000"></textarea>
        </FormRow>

        <FormRow for="info.price" label="Price" :valid="valid('meta.info.price')">
            <NumberField max="9999999" id="info.price" v-model="item.meta.info.price" prefix="$" :showLabel="false" />
        </FormRow>

        <FormRow for="info.rate" label="Advertised Rate" :valid="valid('meta.info.rate')">
            <TextInput id="info.rate" v-model="item.meta.info.rate" maxlength="20" size="20" />
        </FormRow>

        <FormRow for="info.sqft" label="Square Feet" :valid="valid('meta.info.sqft')">
            <NumberField max="9999" id="info.sqft" width="w-20" v-model="item.meta.info.sqft" :showLabel="false" />
        </FormRow>

        <FormRow for="info.bedrooms" label="Bedrooms" :valid="valid('meta.info.bedrooms')">
            <NumberField max="10" id="info.bedrooms" width="w-12" v-model="item.meta.info.bedrooms" :showLabel="false" />
        </FormRow>

        <FormRow for="info.bathrooms.full" label="Full Bathrooms" :valid="valid('meta.info.bathrooms.full')">
            <NumberField max="10" id="info.bathrooms.full" width="w-12" v-model="item.meta.info.bathrooms.full" :showLabel="false" />
        </FormRow>

        <FormRow for="info.bathrooms.half" label="Half Bathrooms" :valid="valid('meta.info.bathrooms.half')">
            <NumberField max="10" id="info.bathrooms.half" width="w-12" v-model="item.meta.info.bathrooms.half" :showLabel="false" />
        </FormRow>

        <FormRow for="info.garages" label="Garages" :valid="valid('meta.info.garages')">
            <NumberField max="10" step=".5" id="info.garages" width="w-12" v-model="item.meta.info.garages" :showLabel="false" />
        </FormRow>

        <FormRow for="info.geo" label="Geolocation" :valid="[...valid('meta.info.geo.lat'), ...valid('meta.info.geo.lng')]">
            <p class="grid gap-2">
                <label for="info.geo.lat" class="text-sm leading-5 font-medium text-oakwood-gray-500" :class="{'text-oakwood-red' : valid('meta.info.geo.lat').length}">Latitude</label>
                <NumberField suffix="º" id="info.geo.lat" width="w-full" step="0.00000000000001" :max="90" :min="-90" v-model="item.meta.info.geo.lat" />
            </p>
            <p class="grid gap-2">
                <label for="info.geo.lng" class="text-sm leading-5 font-medium text-oakwood-gray-500" :class="{'text-oakwood-red' : valid('meta.info.geo.lng').length}">Longitude</label>
                <NumberField suffix="º" id="info.geo.lng" width="w-full" step="0.00000000000001" :max="180" :min="-180" v-model="item.meta.info.geo.lng" />
            </p>
        </FormRow>

        <FormRow for="info.callout" label="Callout" :valid="valid('meta.info.callout')">
            <SelectField id="info.callout" v-model="item.meta.info.callout" :allowNull="false" :options="callouts"/>
        </FormRow>

        <FormRow for="info.website" label="Home Website" :valid="valid('meta.info.website')">
            <URLField id="info.website" v-model="item.meta.info.website" class="w-full" />
        </FormRow>

        <FormRow for="info.notes" label="Internal Notes" :valid="valid('meta.info.notes')">
            <textarea class="border border-oakwood-gray-200 w-full p-2 rounded focus:border-bhav-blue-400 min-h-48" id="info.notes" v-model="item.meta.info.notes"></textarea>
        </FormRow>

    </fieldset>
       
</template>

<script>
import { formatToCurrency } from '@/helpers/numberHelpers';
import {NumberField, FormButton, FormRow, SelectField, URLField, TextInput} from '@/components/form';
import { GlobalPolicy } from '@/policies/global';
import axios from 'axios';
import { useMessageStore } from '@/stores/message';
import { mapActions, mapState } from 'pinia';

export default {
    setup() {
        return {
            GlobalPolicy
        }
    },
    data() {
        return {
            item: {
                slug: null,
                meta: {
                    info: {
                        bedrooms: 0,
                        bathrooms: {
                            half: 0,
                            full: 0
                        },
                        garages: 0,
                        sqft: 0,
                        geo: {
                            lat: 0,
                            lng: 0
                        },
                        description: null,
                        callout: 'N/S',
                        price: 0,
                        rate: null,
                        headline: null,
                        website: null,
                        notes: null
                    }
                },
                sales_order_rid: null,
                floorplan_name: null,
                collection_name: null
            },
            editing: false,
            callouts: [
                { label: 'Not Selected', value: 'N/S' },
                { label: 'New Floorplan', value: 'New Floorplan' },
                { label: 'Popular Floorplan', value: 'Popular Floorplan' },
                { label: 'Price Reduced', value: 'Price Reduced' },
                { label: '3-Car Garage', value: '3-Car Garage' },
                { label: 'Finished Basement', value: 'Finished Basement' },
                { label: 'Last One', value: 'Last One' },
                { label: 'Golf Course', value: 'Golf Course' },
                { label: 'Backs to Open Space', value: 'Backs to Open Space' },
                { label: 'Next to Park', value: 'Next to Park' },
                { label: 'Included Upgrades', value: 'Included Upgrades' },
                { label: 'Special Financing', value: 'Special Financing' },
                { label: 'Model Home', value: 'Model Home' },
                { label: 'Under Construction', value: 'Under Construction' }
            ]
        }
    },
    computed: {
        ...mapState(useMessageStore, ['errors'])
    },
    methods: {
        ...mapActions(useMessageStore, ['clear']),
        edit() {
            this.editing = true;
            this.item = JSON.parse(JSON.stringify(this.salesOrder));
        },
        cancel() {
            this.clear();
            this.editing = false;
        },
        save() {
            axios.patch(`sales_orders/${this.salesOrder.id}`, this.item)
                .then(resp => {
                    this.$emit('update', this.item);
                    this.cancel();
                })
                .catch(e => {
                    throw e;
                });
        },
        valid(field) {
            return this?.errors[field]?.length ?? 0 ? this.errors[field] : [];
        },
        formatToCurrency
    },
    emits: [
        'update'
    ],
    props: {
        salesOrder: {
            type: Object,
            required: true
        }
    },
    components: {
        FormButton,
        FormRow,
        NumberField,
        SelectField,
        URLField,
        TextInput
    }
}
</script>